import getEnv from '@clearscore/helpers.envs';
import getRelease from '@clearscore/helpers.release';

const GLOBAL_NAMESPACE = '__cs__';

// only available when SENTRY_ID is passed
if (getEnv('SENTRY_ID') && window.Sentry) {
    const { Sentry } = window;

    const release = getRelease();

    const configuration = {
        dsn: `https://${getEnv('SENTRY_ID')}`,
    };

    if (release && release.release) configuration.release = release.release;
    if (release && release.env) configuration.environment = release.env;
    if (getEnv('SENTRY_MAX_BREADCRUMBS')) configuration.maxBreadcrumbs = getEnv('SENTRY_MAX_BREADCRUMBS');
    if (getEnv('SENTRY_DEBUG')) configuration.sampleRate = getEnv('SENTRY_DEBUG');
    if (getEnv('SENTRY_SAMPLE_RATE')) configuration.debug = getEnv('SENTRY_SAMPLE_RATE');

    // https://docs.sentry.io/error-reporting/configuration/?platform=browsernpm
    Sentry.init(configuration);

    const monitoring = {};

    /**
     * attach helpers to global namespace __cs__.monitoring
     */
    if (window[GLOBAL_NAMESPACE] && !window[GLOBAL_NAMESPACE].monitoring)
        window[GLOBAL_NAMESPACE].monitoring = monitoring;

    /**
     * set user properties
     * @param {any} uuid
     * @summary https://docs.sentry.io/enriching-error-data/context/?platform=browsernpm#capturing-the-user
     */
    monitoring.identity = (uuid) => {
        if (uuid) {
            Sentry.configureScope((scope) => {
                scope.setUser({ id: uuid });
            });
        }
    };

    /**
     * set tag against user
     * @param {object} name
     * @param {object} value
     * @summary https://docs.sentry.io/enriching-error-data/context/?platform=browsernpm#tagging-events
     */
    monitoring.tag = (name, value) => {
        if (name && value) {
            Sentry.configureScope((scope) => {
                scope.setTag(name, value);
            });
        }
    };

    /**
     * set a property name to show against logging
     * @param {object} name
     * @param {object} value
     * @summary https://docs.sentry.io/enriching-error-data/context/?platform=browsernpm#extra-context
     */
    monitoring.property = (name, value) => {
        if (name && value) {
            Sentry.configureScope((scope) => {
                scope.setExtra(name, value);
            });
        }
    };

    /**
     * add use breadcrumb to logging
     * @param {object} data
     * @param {object} data.category
     * @param {object} data.message
     * @param {object} data.level
     * @summary https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=browsernpm
     * @example
     * {
     *   category: 'auth',
     *    message: `Authenticated user`,
     *    level: 'info',
     * }
     */
    monitoring.addBreadcrumb = (data) => {
        if (data) {
            Sentry.addBreadcrumb({
                level: 'info',
                ...data,
            });
        }
    };

    /**
     * log an error
     * @param {*} err
     * @summary https://docs.sentry.io/error-reporting/capturing/?platform=browser#capturing-errors--exceptions
     */
    monitoring.error = (err) => {
        if (err) {
            Sentry.captureException(err);
        }
    };

    /**
     * log a message
     * @param {string} msg
     * @summary https://docs.sentry.io/error-reporting/capturing/?platform=browser#capturing-messages
     */
    monitoring.log = (msg) => {
        if (msg) {
            Sentry.captureMessage(msg);
        }
    };

    /**
     * log a message
     * @param {string} msg
     * @summary https://docs.sentry.io/error-reporting/capturing/?platform=browser#capturing-messages
     */
    monitoring.withScope = (func) => {
        if (func) {
            Sentry.withScope(func);
        }
    };

    /**
     * Set vendor and core versions as searchable tags against all logs
     */
    if (release.core) monitoring.tag('core', release.core);
    if (release.vendor) monitoring.tag('vendor', release.vendor);
}
